import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Users, Wine, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";

const fmt = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export const Sections = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/sections").then((r) => setData(r.data)).catch(() => setData({ enabled: false }));
  }, []);

  if (!data || !data.enabled) return null;

  const allOut = !!data.sold_out_all;
  const isEp3 = data.event_slug === "ep3-nov28";

  return (
    <section id="sections" className="py-24 md:py-32 px-6 md:px-10 border-t border-white/5" data-testid="sections">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">
          <Wine className="w-4 h-4" /> VIP Sections & Bottle Service
        </div>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-[0.95]">
          Claim your <span className="italic text-gradient-gold">section</span>
        </h2>
        {allOut ? (
          <p className="text-[#A19D94] text-sm md:text-base mt-4 max-w-2xl leading-relaxed" data-testid="sections-soldout-note">
            {data.event}. <span className="text-[#F4D77E]">Every section is spoken for</span> — the floor is where the party's at.
            Already reserved? <a href="/reservation" className="text-[#D4AF37] underline underline-offset-4">Manage your reservation</a>.
          </p>
        ) : (
          <p className="text-[#A19D94] text-sm md:text-base mt-4 max-w-2xl leading-relaxed">
            {data.event}. {isEp3
              ? "Every section price includes admission — seating only, no bottle service required. Tap a section to lock it in."
              : "Tap any section on the legend to see its capacity, bottle minimums, and lock it in with a deposit. Bottle service includes mixers — admission tickets are sold separately."}
          </p>
        )}

        <div className="mt-12 border border-[#D4AF37]/30 gold-glow overflow-hidden" data-testid="floorplan-frame">
          <img src={isEp3 ? "/sections/floorplan-ep3.jpg" : "/sections/floorplan.jpg"} alt="Jay Atk Presents — event floor plan, 2617 Washington Ave" className="w-full" data-testid="floorplan-image" />
        </div>

        <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#8C7324]" data-testid="legend-label">
          <MapPin className="w-3.5 h-3.5" /> {allOut ? "All sections sold out" : isEp3 ? "Chocolate City sections — tap to reserve" : "Legend — tap a section to reserve"}
        </div>
        {data.sections.length === 0 && (
          <div className="mt-6 border border-[#D8B26E]/30 bg-black/40 p-8 text-center" data-testid="sections-dropping-soon">
            <p className="font-serif-display text-2xl text-[#F4E7C0]">Section layout dropping soon.</p>
            <p className="text-sm text-[#A19D94] mt-2">Chocolate City VIP sections are being finalized — check back shortly.</p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="sections-grid">
          {data.sections.map((s, i) => {
            const reserved = allOut || s.remaining === 0;
            return (
              <motion.button key={s.id} onClick={() => !reserved && setSelected(s)}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.03, duration: 0.5 }}
                disabled={reserved} data-testid={`section-card-${s.id}`}
                className={`text-left border p-5 transition-colors duration-300 ${
                  reserved
                    ? "border-white/5 bg-white/[0.02] opacity-45 cursor-not-allowed"
                    : "border-white/10 bg-black/40 hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/5 cursor-pointer"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-serif-display text-2xl text-[#FDFBF7]">{isEp3 ? s.name : s.id === "RAIL_SEAT" ? "RAIL" : s.id === "RAIL_FULL" ? "RAIL" : s.id}</span>
                  {reserved
                    ? <span className="text-[9px] uppercase tracking-[0.2em] text-red-400 border border-red-400/40 px-2 py-1" data-testid={`section-reserved-${s.id}`}>{allOut && !s.reserved ? "Sold Out" : "Reserved"}</span>
                    : <span className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1" data-testid={`section-deposit-${s.id}`}>{fmt(s.deposit)}{s.per_guest ? " /guest" : s.seat ? " /seat" : ""}</span>}
                </div>
                <div className="text-xs text-[#A19D94] mt-2 leading-snug">{isEp3 ? s.area : s.name}</div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#5c5850] mt-2">
                  <Users className="w-3 h-3" />
                  {s.seat ? `${s.remaining} of 10 seats left` : `Up to ${s.guests} guests`}
                  {!reserved && (s.tiered ? <span className="text-[#8C7324]">· 2–3 bottle min</span> : s.min_bottles ? <span className="text-[#8C7324]">· {s.min_bottles} bottle min</span> : null)}
                  {!reserved && isEp3 && s.capacity > 1 && <span className="text-[#8C7324]">· {s.remaining} left</span>}
                </div>
              </motion.button>
            );
          })}
        </div>

        {(data.display_only || []).length > 0 && (
        <div className="mt-6 border border-white/10 bg-black/30 p-5" data-testid="display-only-row">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#8C7324]">Also on the floor</span>
          <div className="mt-2 space-y-1">
            {data.display_only.map((d) => (
              <p key={d.ids} className="text-xs text-[#A19D94]"><span className="text-[#FDFBF7] font-semibold">{d.ids}</span> — {d.label}. <span className="text-[#8C7324]">{d.note}.</span></p>
            ))}
          </div>
          <p className="text-[11px] text-[#5c5850] mt-3">Deposits are non-refundable. Bottle balances are due in full by {data.balance_due} (72 hours before the event). Unpaid reservations are subject to cancellation.</p>
        </div>
        )}
      </div>

      <SectionModal section={selected} data={data} onClose={() => setSelected(null)} />
    </section>
  );
};

const inputCls = "w-full bg-transparent border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors";

const SectionModal = ({ section, data, onClose }) => {
  const [partySize, setPartySize] = useState(0);
  const [seatQty, setSeatQty] = useState(1);
  const [picks, setPicks] = useState({}); // bottle id -> qty
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (section) {
      setPartySize(section.seat ? 1 : section.tiered ? 5 : section.guests);
      setSeatQty(1);
      setPicks({});
      setName(""); setEmail(""); setPhone("");
    }
  }, [section]);

  const minBottles = useMemo(() => {
    if (!section) return 0;
    return section.tiered ? (partySize >= 9 ? 3 : 2) : (section.min_bottles || 0);
  }, [section, partySize]);

  const bottleCount = Object.values(picks).reduce((a, b) => a + b, 0);
  const bottlesTotal = Object.entries(picks).reduce((sum, [id, qty]) => {
    const item = data?.bottle_menu.flatMap((c) => c.items).find((i) => i.id === id);
    return sum + (item ? item.price * qty : 0);
  }, 0);
  const depositToday = section ? (section.per_guest ? section.deposit * partySize : section.deposit * (section.seat ? seatQty : 1)) : 0;
  const canReserve = section && bottleCount >= minBottles && name.trim() && email.includes("@") && !busy;

  const bump = (id, delta) => {
    setPicks((p) => {
      const next = { ...p, [id]: Math.max(0, Math.min(10, (p[id] || 0) + delta)) };
      if (!next[id]) delete next[id];
      return next;
    });
  };

  const reserve = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/sections/checkout", {
        section_id: section.id, event: data.event_slug || "pglr-oct4",
        name: name.trim(), email: email.trim(), phone: phone.trim(),
        party_size: partySize, quantity: seatQty,
        bottles: Object.entries(picks).map(([id, qty]) => ({ id, qty })),
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start the reservation.");
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {section && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={onClose} data-testid="section-modal-overlay">
          <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[#D4AF37]/40 bg-[#0A0A0A] gold-glow"
            onClick={(e) => e.stopPropagation()} data-testid="section-modal">
            <button onClick={onClose} className="absolute top-4 right-4 z-10 text-[#A19D94] hover:text-[#D4AF37] transition-colors" data-testid="section-modal-close">
              <X className="w-5 h-5" />
            </button>

            <div className="p-7 md:p-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C7324]">{section.area}</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#FDFBF7] mt-2" data-testid="section-modal-title">{section.name}</h3>
              <div className="flex flex-wrap gap-4 mt-3 text-[11px] uppercase tracking-[0.18em] text-[#A19D94]">
                <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#D4AF37]" /> {section.seat ? "1 guest per seat" : `Up to ${section.guests} guests`}</span>
                <span className="inline-flex items-center gap-1.5"><Wine className="w-3.5 h-3.5 text-[#D4AF37]" /> <span data-testid="section-min-bottles">{minBottles > 0 ? `${minBottles} bottle minimum` : "Bottles optional"}</span></span>
                {section.per_guest && <span className="inline-flex items-center gap-1.5 text-[#F4D77E]" data-testid="section-per-guest">{fmt(section.deposit)} per guest · includes admission</span>}
              </div>
              {data.event_slug === "ep3-nov28" && (
                <p className="text-[11px] text-[#8C7324] mt-3 uppercase tracking-[0.15em]" data-testid="ep3-section-note">
                  All section prices include admission · seating only · no bottle service required
                </p>
              )}

              {section.tiered && (
                <div className="mt-6" data-testid="party-size-block">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324] block mb-2">How many in your party?</label>
                  <select value={partySize} onChange={(e) => setPartySize(parseInt(e.target.value, 10))} data-testid="party-size-select"
                    className="bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] text-sm focus:outline-none focus:border-[#D4AF37]">
                    {Array.from({ length: section.guests - 4 }, (_, i) => i + 5).map((n) => (
                      <option key={n} value={n}>{n} guests — {n >= 9 ? "3" : "2"} bottle minimum</option>
                    ))}
                  </select>
                </div>
              )}
              {section.seat && (
                <div className="mt-6">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324] block mb-2">How many rail seats?</label>
                  <select value={seatQty} onChange={(e) => setSeatQty(parseInt(e.target.value, 10))} data-testid="seat-qty-select"
                    className="bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] text-sm focus:outline-none focus:border-[#D4AF37]">
                    {Array.from({ length: section.remaining }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} seat{n > 1 ? "s" : ""} — {fmt(section.deposit * n)} deposit</option>
                    ))}
                  </select>
                </div>
              )}
              {!section.seat && !section.tiered && section.guests > 1 && (
                <div className="mt-6" data-testid="party-size-block">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324] block mb-2">How many in your party?</label>
                  <select value={partySize} onChange={(e) => setPartySize(parseInt(e.target.value, 10))} data-testid="party-size-select"
                    className="bg-black border border-white/20 px-4 py-3 text-[#FDFBF7] text-sm focus:outline-none focus:border-[#D4AF37]">
                    {Array.from({ length: section.guests }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} guest{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
              )}

              {data.event_slug !== "ep3-nov28" && (
              <div className="mt-8" data-testid="bottle-picker">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Choose your bottles</span>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#A19D94]" data-testid="bottle-count">
                    {minBottles > 0 ? `${bottleCount} of ${minBottles} required` : `${bottleCount} selected · optional`}
                  </span>
                </div>
                <p className="text-[11px] text-[#5c5850] mb-4">Mixers included with every bottle. Bottles are paid {data.balance_due} — only the deposit is charged today.</p>
                {data.bottle_menu.map((cat) => (
                  <div key={cat.category} className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7324] border-b border-white/10 pb-2 mb-2">{cat.category}</div>
                    {cat.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between py-2" data-testid={`bottle-row-${item.id}`}>
                        <div>
                          <span className="text-sm text-[#FDFBF7]">{item.name}</span>
                          <span className="text-xs text-[#8C7324] ml-3">{fmt(item.price)}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => bump(item.id, -1)} disabled={!picks[item.id]} data-testid={`bottle-minus-${item.id}`}
                            className="w-7 h-7 border border-white/20 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 flex items-center justify-center">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-4 text-center text-sm text-[#FDFBF7]" data-testid={`bottle-qty-${item.id}`}>{picks[item.id] || 0}</span>
                          <button onClick={() => bump(item.id, 1)} data-testid={`bottle-plus-${item.id}`}
                            className="w-7 h-7 border border-white/20 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="section-name-input" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" className={inputCls} data-testid="section-email-input" />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className={inputCls} data-testid="section-phone-input" />
              </div>

              <div className="mt-6 border border-white/10 bg-black/40 p-5 space-y-2" data-testid="section-totals">
                <div className="flex justify-between text-sm"><span className="text-[#A19D94]">{data.event_slug === "ep3-nov28" ? "Section — includes admission, due today" : "Deposit — due today"}</span><span className="text-[#F4D77E] font-semibold" data-testid="total-deposit">{fmt(depositToday)}</span></div>
                {data.event_slug !== "ep3-nov28" && (
                <div className="flex justify-between text-sm"><span className="text-[#A19D94]">Bottles — due {data.balance_due}</span><span className="text-[#FDFBF7]" data-testid="total-bottles">{fmt(bottlesTotal)}</span></div>
                )}
                <div className="flex justify-between text-sm border-t border-white/10 pt-2"><span className="text-[#A19D94] uppercase tracking-[0.15em] text-[11px]">Reservation total</span><span className="text-[#F4D77E] font-serif-display text-xl" data-testid="total-full">{fmt(depositToday + bottlesTotal)}</span></div>
              </div>

              <button onClick={reserve} disabled={!canReserve} data-testid="reserve-button"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wine className="w-4 h-4" />}
                {busy ? "Redirecting…" : bottleCount < minBottles ? `Select at least ${minBottles} bottle${minBottles > 1 ? "s" : ""}` : `Reserve — pay ${fmt(depositToday)} deposit`}
              </button>
              <p className="text-[10px] text-[#5c5850] mt-3 text-center">Deposits are non-refundable. Admission tickets sold separately. 21+ only.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
